import CustomTable from '@/components/CustomTable/CustomTable.vue';
import CustomTabs from '@/components/CustomTab/CustomTab.vue';
import NavBar from '@/components/NavBar/NavBar.vue';
import { format_date } from '@/utils/date_formatter';
import { validate_first_name, validate_last_name, validate_email, validate_phone_number, validate_invalid_characters} from '@/utils/validations.js';
import {show_error} from '@/utils/validations.js';
import { API_BASE_URL } from "@/config";
import { validateToken } from '@/utils/auth';
import { LOGOUT } from "@/utils/logout.js";
import { useToast } from "@/components/Toast/useToast";
import Swal from 'sweetalert2';

const ROLE_MAP = {
  Clients: 1,
  Customers: 3,
  ClientAdmins: 4,
};

export default {
  name: 'ClientAdminUsers',
  mixins: [LOGOUT],
  setup() {
    const { showSuccess, showError, showWarning } = useToast();
    
    return {
      showSuccess,
      showError,
      showWarning
    };
  },
  components: {
    NavBar,
    CustomTable,
    CustomTabs
  },
  data: () => ({
    currentTab: localStorage.getItem('client_users_tab') || 'ClientAdmins', 
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    role: '',
    isFirstNameValid: false,
    isLastNameValid: false,
    isEmailValid: false,
    isPhoneNumberValid: false,
    columns: [
      { label: 'Name', key: 'name_email' },
      { label: 'Phone', key: 'phone_number' },
      { label: 'Date Joined', key: 'date_joined' },
    ],
    tabs: [
      { label: 'Client Admins', value: 'ClientAdmins' },
      { label: 'Clients', value: 'Clients' },
      { label: 'Customers', value: 'Customers' }
    ]
  }),
  computed: {
    isFormValid() {
      return this.isFirstNameValid && 
             this.isLastNameValid && 
             this.isEmailValid && 
             this.isPhoneNumberValid && 
             this.role !== '';
    }
  },
  watch: {
    currentTab(newTab) {
      localStorage.setItem('client_users_tab', newTab);
      localStorage.removeItem(`page_client_users_${newTab}`);
    }
  },
  methods: {
    formatDate(date) {
      return format_date(date);
    },
    getUsers(page = 1, search = '') {
      const TOKEN = localStorage.getItem('token');
      const role_id = ROLE_MAP[this.currentTab];
      return new Promise((resolve, reject) => {
        let url = `${API_BASE_URL}/verify_api/workflow_management_api/get_client_admin_users/?page=${page}&role_id=${role_id}`;
        if (search) {
          url += `&search=${encodeURIComponent(search)}`;
        }
        $.ajax({
          url,
          method: 'GET',
          dataType: 'json',
          headers: { Authorization: `Token ${TOKEN}` },
          success: function (data) {
            resolve({
              count: data.count,
              results: data.results,
            });
          },
          error: function (xhr, status, error) {
            reject(error);
          }
        });
      });
    },
    validate_inputs() {
      if (!validate_first_name(this.first_name)) return false;
      if (!validate_last_name(this.last_name)) return false;
      if (!validate_email(this.email)) return false;
      if (!validate_phone_number(this.phone_number)) return false;
      
      return true;
    },
    validate_phone_number_input() {
      this.phone_number = this.phone_number.replace(/\D/g, '');
      if (this.phone_number.length > 10) {
        this.phone_number = this.phone_number.substring(0, 10);
      }
      this.isPhoneNumberValid = validate_phone_number(this.phone_number);
    },
    validate_email_input() {
      this.isEmailValid = validate_email(this.email);
    },
    validate_name(field_name) {
      this[field_name] = validate_invalid_characters(this[field_name]);
      if (field_name === 'first_name') {
        this.isFirstNameValid = validate_first_name(this.first_name);
      } else if (field_name === 'last_name') {
        this.isLastNameValid = validate_last_name(this.last_name);
      }
    },
    add_user() {
      const TOKEN = localStorage.getItem('token');
      const BASE_URL = API_BASE_URL;
      if (!BASE_URL) {
        show_error('.error-message', 'Server configuration error. Please try again later.');
        return;
      }
      const ADD_USER_URL = `${BASE_URL}/verify_api/system_management_api/add_user_api/`;
      const FORM_DATA = {
        first_name: this.first_name,
        last_name: this.last_name,
        email: this.email,
        phone_number: this.phone_number,
        role: this.role
      };
      $.ajax({
        method: "POST",
        url: ADD_USER_URL,
        data: JSON.stringify(FORM_DATA),
        contentType: 'application/json',
        headers: {
          Authorization: `Token ${TOKEN}`
        },
        beforeSend: () => {

        },
        success: (response) => {
          this.showSuccess(response.status);
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        },
        error: (error) => {
          this.showError(error.responseJSON.error || 'An error occurred while adding the user');
        }
      });
    },
    view_customer_data(customer_id){
      this.$router.push({
        name: 'view_customer', 
        params: { user_id: customer_id }
       });
      },

    view_client_admin(user_id) {
      this.$router.push({ 
        name: 'view_client_admin',
        params: { user_id: user_id }
      });
    },
  
    view_client(user_id) {
      this.$router.push({ 
        name: 'view_client',
        params: { user_id: user_id }
      });
    },
    
    toggleUserStatus(row) {
      const action = row.is_active ? 'deactivate' : 'activate';
      const actionText = row.is_active ? 'Deactivate' : 'Activate';
      
      Swal.fire({
        title: `${actionText} User?`,
        text: `Are you sure you want to ${action} ${row.first_name} ${row.last_name}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: row.is_active ? '#d33' : '#28a745',
        cancelButtonColor: '#6c757d',
        confirmButtonText: `Yes, ${action}!`,
        cancelButtonText: 'Cancel'
      }).then((result) => {
        if (result.isConfirmed) {
          this.performUserStatusToggle(row.id, action);
        }
      });
    },

    performUserStatusToggle(userId, action) {
      const TOKEN = localStorage.getItem('token');
      const endpoint = action === 'deactivate' ? 'deactivate_user_api' : 'activate_user_api';
      const url = `${API_BASE_URL}/verify_api/verify_admin_api/${endpoint}/${userId}`;

      $.ajax({
        url: url,
        method: 'POST',
        headers: { Authorization: `Token ${TOKEN}` },
        success: (response) => {
          this.showSuccess(response.message);
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        },
        error: (error) => {
          this.showError(error.responseJSON?.error || `Failed to ${action} user`);
        }
      });
    }
  },
  mounted() {
    if (!validateToken(this.$router)) {
        return;
    }
    this.getUsers();
  }
};
